import { useState, useCallback, useEffect, useRef } from "react";
import DeviceUpdate from '../DeviceUpdate'
import { Modal, notification, Popover, Select, Button, Spin, Tag, Space } from 'antd';
import { KeyVal } from "../../Layouts";
import { useRequest } from "../../../hooks";
import { searchDevices, updateDevice, createDevices } from "../../../api/device";
import interact from 'interactjs';
import styles from './deviceMap.module.css';
import { useApp } from "../../../model";
import { throttle } from "lodash";
import { useAuthInfo } from "../../../hooks";
import { DEVICE_SN_PREFIX, deviceDefaultValues } from "../../../constants";
import classnames from 'classnames';

export const useData = (project: Project, refreshKey: Symbol, isHistory?: boolean) => {
    const fetchDevices = useCallback(() => {
        return searchDevices(project.projectId, {
            pageSize: 1000,
            pageNum: 1
        }, isHistory);
    }, [project, refreshKey, isHistory]);
    return useRequest<PagedData<DeviceRecord>>(fetchDevices)
}

const getImgAspectRatio = (url: string) => {
    const img = new Image()
    img.src = url;
    return img.width / img.height;
}


export default function DeviceMap({ project, imgUrl, isHistory = false }: { project: Project, imgUrl: string, isHistory?: boolean }) {
    const app = useApp()
    const { isObserver } = useAuthInfo()
    const [showCreate, setShowCreate] = useState(false);
    const [showPop, setShowPop] = useState(false);
    const [currentPointLocation, setCurrnetpointLocation] = useState<{ x: number, y: number }>();
    const [showCreatingPoint, setShowCreatingPoint] = useState(false);
    const [projectInfo, setProjectInfo] = useState(project);
    const { data, loading } = useData(projectInfo, app.deviceMapDataSourceRefreshKey, isHistory);
    const [creatingDevice, setCreatingDevice] = useState(false);
    const boundDevices = data?.data.filter(i => !!i.location);
    const [bindingDevice, setBindingDevice] = useState<DeviceRecord>();
    const [binding, setBinding] = useState(false);
    const maxScale = 5;
    const minScale = 0.5;
    const [scale, _setScale] = useState(1);
    const setScale = (newScale: number) => {
        if (newScale > maxScale) {
            _setScale(maxScale)
        } else if (newScale < minScale) {
            _setScale(minScale)
        } else {
            _setScale(newScale)
        }
    }
    const [deleting, setDeleting] = useState(false);
    const positionRef = useRef({ x: 0, y: 0 });
    const imgAspectRatioRef = useRef(1)
    const scaleRef = useRef(scale)
    const imgOriginWidthRef = useRef(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const [imgReady, setImgReady] = useState(false);
    useEffect(() => {
        if (containerRef.current) {
            imgOriginWidthRef.current = containerRef.current.clientWidth;
        }
    }, [])
    useEffect(() => {
        scaleRef.current = scale;
    }, [scale])
    const onWheel = useCallback(
        throttle((e: WheelEvent) => {
            if (showPop) return;
            const scale = scaleRef.current;
            if (e.deltaY > 0) {
                setScale(scale * 1.05)
            } else {
                setScale(scale / 1.05)
            }
        }, 5)
        , [showPop])
    useEffect(() => {
        imgAspectRatioRef.current = getImgAspectRatio(imgUrl);
    }, [imgUrl]);
    useEffect(() => {
        if (!containerRef.current || !positionRef.current) return;
        const target = containerRef.current;
        const imgOriginWidth = imgOriginWidthRef.current;
        Object.assign(target.style, {
            width: `${imgOriginWidth * scale}px`,
            transform: `translate(${positionRef.current.x * scale}px, ${positionRef.current.y * scale}px)`
        })
    }, [scale])
    useEffect(() => {
        if (!imgReady) return;
        interact(`#draggableContainer`).draggable({
            listeners: {
                move(event) {
                    const position = positionRef.current;
                    if (!position) return;
                    position.x += event.dx
                    position.y += event.dy
                    const scale = Number((event.target as HTMLDivElement).dataset['scale'] || 1);
                    event.target.style.transform =
                        `translate(${position.x * scale}px, ${position.y * scale}px)`
                }
            }
        });
    }, [imgReady]);
    return (
        <Spin spinning={loading}>
            <div
                id="draggableContainer"
                className={styles.container}
                ref={containerRef}
                data-scale={scale}
                onMouseEnter={() => {
                    document.documentElement.style.overflow = 'hidden'
                }}
                onMouseLeave={() => {
                    document.documentElement.style.overflow = 'unset'
                }}
                onWheel={e => {
                    onWheel(e as unknown as WheelEvent)
                }}
                onContextMenu={e => {
                    if (isObserver || isHistory) return;
                    e.preventDefault();
                    setShowPop(false);
                    const { offsetX, offsetY } = e.nativeEvent;
                    const { offsetWidth, offsetHeight } = containerRef.current!;
                    setCurrnetpointLocation({ x: offsetX / offsetWidth, y: offsetY / offsetHeight });
                    setShowCreatingPoint(true);
                    setTimeout(() => {
                        setShowPop(true);
                    }, 100);
                }}
            >
                <img onLoad={e => {
                    const ele = (e.target as HTMLImageElement)
                    imgAspectRatioRef.current = ele.offsetWidth / ele.offsetHeight;
                    setImgReady(true);
                }} className={styles.img}
                    src={imgUrl} alt="" />
                {
                    boundDevices?.map((device, index) => {
                        const [left, top] = device.location.split(',').map(i => Number(i));
                        return (
                            <Popover key={index} title='设备信息' content={
                                <div>
                                    <KeyVal name='展位号' value={device.name} />
                                    <KeyVal name='设备号' value={device.device?.deviceSn} />
                                    <KeyVal name='设备类型' value={device.device?.type} />
                                    {
                                        !!device.gatewayMac && <KeyVal name='MAC地址' value={device.gatewayMac} />
                                    }
                                    <KeyVal name='设备状态' value={device.device?.online ?
                                        <span style={{ color: 'green' }}>在线</span>
                                        :
                                        <span style={{ color: 'grey' }}>离线</span>
                                    } />
                                    <div style={{ marginBottom: 20 }}>
                                        {
                                            !!device.isKeepOnline && <Tag>24小时保电</Tag>
                                        }
                                        {
                                            !!device.isPowerSupply && <Tag>动力电源</Tag>
                                        }
                                    </div>
                                    {
                                        isHistory ? null :
                                            <Button disabled={isObserver} loading={deleting} size='small' type='default' onClick={() => {
                                                setDeleting(true)
                                                updateDevice(String(project.projectId), device.device?.deviceSn, {
                                                    ...device,
                                                    location: null as any
                                                })
                                                    .then(() => {
                                                        setDeleting(false)
                                                        setShowPop(false)
                                                        setProjectInfo({ ...projectInfo })
                                                    })
                                                    .catch(() => {
                                                        setDeleting(false)
                                                    })
                                            }}>删除</Button>
                                    }
                                </div>
                            }>
                                <div style={{
                                    left: left * 100 + '%', top: top * 100 + '%',
                                }} className={
                                    classnames({
                                        [styles['existing-point']]: true,
                                        [styles['online-point']]: device.device?.online,
                                        [styles['offline-point']]: !device.device?.online,
                                        [styles['error-point']]: device.device?.isAlarm && device.device?.online,
                                    })
                                }>
                                    <div className={styles.tags}>
                                        {
                                            !!device.isKeepOnline && <div>24H</div>
                                        }
                                        {
                                            !!device.isPowerSupply && <div>D</div>
                                        }
                                    </div>
                                </div>
                            </Popover>
                        )
                    })
                }
                <Popover title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>绑定设备</span>
                        <span style={{ fontSize: 12, color: '#333' }}>{boundDevices?.length}/{data?.data.length}已绑定</span>
                    </div>
                } content={
                    <div>
                        <div style={{ display: 'flex' }}>
                            <Select
                                style={{ width: '100%' }}
                                dropdownRender={(children) => {
                                    return (
                                        <div>
                                            <Button style={{ marginLeft: 20 }} type='link' onClick={() => {
                                                setShowCreate(true)
                                            }}>新建设备</Button>
                                            {children}
                                        </div>
                                    )
                                }}
                                optionFilterProp="label"
                                placeholder='请选择要绑定的设备'
                                showSearch
                                options={
                                    (data?.data || []).map((i) => {
                                        return {
                                            label: i.location ? `${i.name}(已绑定)` : i.name,
                                            value: i.id
                                        }
                                    })
                                }
                                value={bindingDevice?.id}
                                onChange={value => {
                                    setBindingDevice(data?.data?.find(i => i.id === value));
                                }}
                            />
                        </div>
                        <br />
                        <div style={{
                            display: 'flex', justifyContent: 'flex-end'
                        }}>
                            <Button size='small' onClick={() => {
                                setShowPop(false)
                                setShowCreatingPoint(false);
                            }}>取消</Button>
                            <Button disabled={!bindingDevice} loading={binding} size='small' type='primary' onClick={() => {
                                if (!bindingDevice || !currentPointLocation) return;
                                setBinding(true);
                                updateDevice(String(project.projectId), bindingDevice.device?.deviceSn, {
                                    ...bindingDevice,
                                    location: `${currentPointLocation.x},${currentPointLocation.y}`
                                })
                                    .then(() => {
                                        setShowPop(false)
                                        setBindingDevice(undefined)
                                        setProjectInfo({ ...projectInfo })
                                        setShowCreatingPoint(false)
                                        setBindingDevice(undefined);
                                    })
                                    .catch(() => {
                                        notification.error({ message: '绑定失败,请重试' })
                                    }).finally(() => {
                                        setBinding(false)
                                    })

                            }} style={{ marginLeft: 20 }}>确认</Button>
                        </div>
                    </div>
                } open={showPop}>
                    <div style={{
                        left: ((currentPointLocation?.x || 0) * 100) + '%',
                        top: ((currentPointLocation?.y || 0) * 100) + '%',
                        display: showCreatingPoint ? 'block' : 'none'
                    }} className={styles["creating-point"]}></div>
                </Popover>
            </div>
            <Modal destroyOnClose zIndex={99999} footer={null} title='新建设备' open={showCreate} onCancel={() => { setShowCreate(false) }}>
                <DeviceUpdate projectId={project.projectId} submitLoading={creatingDevice} initialValues={{ ...deviceDefaultValues }} onComplete={(values) => {
                    const {
                        description, deviceSn, type, ...basic
                    } = values;
                    setCreatingDevice(true)
                    createDevices(String(project.projectId), [
                        {
                            ...basic,
                            location: `${currentPointLocation?.x},${currentPointLocation?.y}`,
                            device: {
                                deviceSn: DEVICE_SN_PREFIX + deviceSn,
                                description, type
                            }
                        }
                    ])
                        .then(() => {
                            setShowCreate(false)
                            setShowPop(false)
                            setBindingDevice(undefined)
                            setProjectInfo({ ...projectInfo })
                            setShowCreatingPoint(false)
                        })
                        .catch(() => {
                            notification.error({ message: '创建失败,请重试' })
                        })
                        .finally(() => {
                            setCreatingDevice(false)
                        })
                }} />
            </Modal>
        </Spin>
    )
}